import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  makeStyles,
  tokens,
  Badge,
  Avatar,
  Toast,
  ToastTitle,
  ToastBody,
  Toaster,
  useToastController,
  useId,
  Button,
  Input,
} from '@fluentui/react-components';
import DataTable, { ColumnConfig } from '../components/DataTable';
import CustomerFiltersSidebar from '../components/CustomerFiltersSidebar';
import TablePage from '../components/TablePage';
import { ToolbarButton, Tooltip } from '@fluentui/react-components';
import {
  PanelLeft24Regular,
  Person20Filled,
  PersonCall20Filled,
  Building20Filled,
  ArrowSync24Regular,
  Search20Regular,
  Edit20Regular,
  Tag20Regular,
  BarcodeScanner20Regular,
  Delete20Regular,
} from '@fluentui/react-icons';
import { AddCustomerSplitButton } from '../config/toolbars';
import { useCustomerFilters } from '../hooks/useCustomerFilters';
import BulkEditCustomersModal from '../components/BulkEditCustomersModal';
import EditTagsModal from '../components/EditTagsModal';
import GenerateBarcodesModal from '../components/GenerateBarcodesModal';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import CustomerDrawer from '../components/CustomerDrawer';
import CompanyDeleteConflictModal from '../components/CompanyDeleteConflictModal';
import ShareDialog from '../components/ShareDialog';
import ImportCustomersModal from '../components/ImportCustomersModal';
import { useDebug } from '../contexts/DebugContext';
import { getRowMenuForRoute } from '../config/toolbars';
import { queryKeys } from '../lib/queryClient';
import { BOOQABLE_ADMIN_URL } from '../config/booqable';
import { isLocalhost } from '../config/appEnv';
import { apiFetch } from '../lib/apiFetch';
import {
  fetchCustomers,
  deleteCustomer,
  deleteCustomers as deleteCustomersAPI,
  type Customer,
} from '../services/customerService';
import { formatBusinessDate } from '../lib/businessTime';

type ViewMode = 'table' | 'list';

const useStyles = makeStyles({
  tagsContainer: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
  },
  emptyCell: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
});

export default function Customers() {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const toasterId = useId('toaster');
  const { dispatchToast } = useToastController(toasterId);
  const { debugMode } = useDebug();
  const filters = useCustomerFilters();
  // Filter panel collapsed state is owned by useCustomerFilters (persists
  // to localStorage). We deliberately don't reuse useLayout().sidebarOpen
  // because LeftRail's onNavigate forces it back to true on every nav,
  // which would clobber the user's choice to keep the panel closed when
  // they leave and return to /customers.
  const filterPanelCollapsed = filters.state.sidebarCollapsed;
  // Controlled so we can force-dismiss it on click — otherwise on touch the
  // tap focuses the button and the tooltip lingers until you tap elsewhere.
  const [filterTipOpen, setFilterTipOpen] = useState(false);
  const toggleFilterPanel = () => {
    filters.setField('sidebarCollapsed', !filters.state.sidebarCollapsed);
    setFilterTipOpen(false);
  };

  // Live filter for the table — narrows visible rows by name/email/phone.
  // Distinct from the global topbar search (cross-entity navigator).
  const [tableSearch, setTableSearch] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  const [editTagsModalOpen, setEditTagsModalOpen] = useState(false);
  const [generateBarcodesModalOpen, setGenerateBarcodesModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  // Pre-selected entity kind when the drawer opens via the SplitButton's
  // dropdown. undefined means "no preference" (drawer falls back to default).
  const [addInitialKind, setAddInitialKind] = useState<'individual' | 'contact' | 'company' | undefined>(undefined);
  const [deleteCustomerId, setDeleteCustomerId] = useState<string | null>(null);
  const [deleteCustomerKind, setDeleteCustomerKind] = useState<'individual' | 'contact' | 'company' | undefined>(undefined);
  const [deleteSingleModalOpen, setDeleteSingleModalOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareCustomer, setShareCustomer] = useState<Customer | null>(null);
  // Conflict modal state — opens when DELETE returns 409 because the customer
  // is a Company with active Contacts. The user picks reassign-then-delete or
  // cascade delete from inside the modal.
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictCompanyId, setConflictCompanyId] = useState<string | null>(null);
  const [conflictCompanyName, setConflictCompanyName] = useState<string>('');
  const [importModalOpen, setImportModalOpen] = useState(false);

  // Fetch customers using React Query
  const {
    data: customers = [],
    isLoading: loading,
    error,
    isFetching,
    isPlaceholderData,
  } = useQuery({
    queryKey: queryKeys.customers.all,
    queryFn: () => fetchCustomers(true),
    staleTime: 10 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    // Customer data can change out-of-band (Supabase wipes, manual sync, edits
    // from other tabs). Always refetch on mount so the row IDs reflect reality.
    refetchOnMount: 'always',
  });


  // Delete mutation. Optimistically removes from cache and TRUSTS that state
  // until next route mount. We deliberately do NOT invalidate on settle —
  // Booqable has eventual consistency between write and read replicas, and an
  // immediate refetch races the archive propagation, bringing the customer
  // back into the list. Next mount of /customers refetches fresh.
  // This path only fires for individual/contact rows now — companies route
  // through CompanyDeleteConflictModal directly from the row-menu handler.
  // That keeps the optimistic removal safe (no 409 fallback to flicker on)
  // and lets the conflict UI own its own confirmation.
  const deleteMutation = useMutation({
    mutationFn: (customerId: string) => deleteCustomer(customerId),
    onMutate: async (customerId: string) => {
      // Match only the list query — `queryKeys.customers.all` is `['customers']`
      // which would also match `['customers', id]` detail entries (objects,
      // not arrays) and break the array `.filter` updater. Predicate excludes
      // them by checking the cached data is an array.
      const matcher = {
        queryKey: queryKeys.customers.all,
        predicate: (q: any) => Array.isArray(q.state.data),
      };
      await queryClient.cancelQueries(matcher);
      const snapshots = queryClient.getQueriesData<Customer[]>(matcher);
      queryClient.setQueriesData<Customer[]>(matcher, (old) =>
        old ? old.filter((c) => c.id !== customerId) : old,
      );
      return { snapshots };
    },
    onError: (err, _customerId, context) => {
      console.error('✗ Customer delete failed; rolling back:', err);
      context?.snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSuccess: () => console.log('✓ Customer deleted'),
  });

  // Bulk delete — same optimistic pattern as single delete. The service now
  // returns a per-id outcome so we can roll back only the rows that didn't
  // actually delete (409 conflicts, errors) instead of nuking the whole batch.
  const bulkDeleteMutation = useMutation({
    mutationFn: (customerIds: string[]) => deleteCustomersAPI(customerIds),
    onMutate: async (customerIds: string[]) => {
      const matcher = {
        queryKey: queryKeys.customers.all,
        predicate: (q: any) => Array.isArray(q.state.data),
      };
      await queryClient.cancelQueries(matcher);
      const snapshots = queryClient.getQueriesData<Customer[]>(matcher);
      const removeSet = new Set(customerIds);
      queryClient.setQueriesData<Customer[]>(matcher, (old) =>
        old ? old.filter((c) => !removeSet.has(c.id)) : old,
      );
      return { snapshots };
    },
    onError: (err, _customerIds, context) => {
      console.error('✗ Bulk delete failed; rolling back:', err);
      context?.snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSuccess: (results, _customerIds, context) => {
      const skipped = results.filter((r) => r.outcome === 'has_contacts');
      const errored = results.filter((r) => r.outcome === 'error');
      const okIds = new Set(results.filter((r) => r.outcome === 'ok').map((r) => r.id));

      // Reconcile the optimistic state with the actual outcome. onMutate
      // removed every selected id; we now restore the snapshot and re-remove
      // only the ones that actually deleted. This is simpler and less
      // error-prone than appending failed rows back into the post-removal cache.
      if (skipped.length > 0 || errored.length > 0) {
        context?.snapshots.forEach(([key, data]) => {
          if (!Array.isArray(data)) return;
          queryClient.setQueryData<Customer[]>(
            key,
            data.filter((c) => !okIds.has(c.id))
          );
        });
      }

      console.log(`✓ Bulk delete: ${okIds.size} deleted, ${skipped.length} skipped (has contacts), ${errored.length} errored`);

      if (skipped.length > 0) {
        dispatchToast(
          <Toast>
            <ToastTitle>{`Skipped ${skipped.length} ${skipped.length === 1 ? 'company' : 'companies'} with active contacts`}</ToastTitle>
            <ToastBody>
              Open each one individually to reassign or cascade-delete its contacts.
            </ToastBody>
          </Toast>,
          { intent: 'warning' }
        );
      }
      if (errored.length > 0) {
        dispatchToast(
          <Toast>
            <ToastTitle>{`Failed to delete ${errored.length} ${errored.length === 1 ? 'customer' : 'customers'}`}</ToastTitle>
          </Toast>,
          { intent: 'error' }
        );
      }
    },
  });

  // Get row menu configuration for current route
  const rowMenuItems = getRowMenuForRoute(location.pathname);

  // Tooltip text for the kind icon — keeps the column visually compact
  // while still being scannable for screen readers / hover.
  const kindIconFor = (customer: Customer) => {
    if (customer.customerKind === 'company') {
      return { icon: <Building20Filled />, label: 'Company' };
    }
    if (customer.customerKind === 'contact') {
      return { icon: <PersonCall20Filled />, label: 'Contact' };
    }
    return { icon: <Person20Filled />, label: 'Individual' };
  };

  // Customer columns definition
  const customerColumns: ColumnConfig<Customer>[] = [
    {
      columnKey: 'checkbox',
      label: '',
      sortable: false,
      groupable: false,
    },
    {
      columnKey: 'number',
      label: '#',
      sortable: true,
      filterable: false,
      groupable: false,
      width: '50px',
      render: (customer: Customer) => (
        <span style={{ color: tokens.colorNeutralForeground3, fontFamily: 'monospace', fontSize: '12px' }}>
          {customer.number ?? ''}
        </span>
      ),
    },
    {
      columnKey: 'name',
      label: 'Name',
      sortable: true,
      filterable: false,
      groupable: false,
      width: '300px',
      render: (customer: Customer) => {
        const { icon, label } = kindIconFor(customer);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Avatar
              icon={icon}
              color="neutral"
              size={32}
              aria-label={label}
            />
            {/* Real anchor (via react-router's Link) so plain-click does
                same-tab SPA navigation while Ctrl/Cmd-click and middle-
                click open in a new tab — same UX as a typical CRM list. */}
            <RouterLink
              data-tour="customers.nameColumn"
              to={`/customers/${customer.id}`}
              onClick={(e) => e.stopPropagation()}
              style={{
                fontWeight: 500,
                color: 'inherit',
                textDecoration: 'none',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none';
              }}
            >
              {customer.name}
            </RouterLink>
          </div>
        );
      },
    },
    {
      columnKey: 'email',
      label: 'Email',
      sortable: true,
      filterable: false,
      groupable: false,
    },
    {
      // columnKey matches the Customer field so DataTable's sort reads a real
      // value; render shows the name with the email in a tooltip.
      columnKey: 'mainContactName',
      label: 'Main contact',
      sortable: true,
      filterable: false,
      groupable: false,
      width: '180px',
      // Company rows only — the designated main contact for that company.
      // Individuals/contacts and companies without one render an em-dash.
      render: (customer: Customer) =>
        customer.mainContactName ? (
          <Tooltip
            content={customer.mainContactEmail || customer.mainContactName}
            relationship="label"
          >
            <span>{customer.mainContactName}</span>
          </Tooltip>
        ) : (
          <span className={styles.emptyCell}>—</span>
        ),
    },
    {
      columnKey: 'created_at',
      label: 'Created',
      sortable: true,
      filterable: false,
      groupable: false,
      width: '140px',
      render: (customer: Customer) =>
        customer.created_at ? (
          <span>{formatBusinessDate(customer.created_at)}</span>
        ) : (
          <span style={{ color: tokens.colorNeutralForeground3, fontSize: '12px' }}>—</span>
        ),
    },
    {
      columnKey: 'latest_order_at',
      label: 'Latest order',
      sortable: true,
      filterable: false,
      groupable: false,
      width: '140px',
      render: (customer: Customer) =>
        customer.latest_order_at ? (
          <span>{formatBusinessDate(customer.latest_order_at)}</span>
        ) : (
          <span style={{ color: tokens.colorNeutralForeground3, fontSize: '12px' }}>—</span>
        ),
    },
    {
      columnKey: 'balance_due_in_cents',
      label: 'Balance due',
      sortable: true,
      filterable: false,
      groupable: false,
      width: '120px',
      render: (customer: Customer) => {
        const cents = customer.balance_due_in_cents ?? 0;
        const dollars = (cents / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
        });
        return (
          <span style={{ color: cents > 0 ? tokens.colorPaletteRedForeground1 : undefined }}>
            {dollars}
          </span>
        );
      },
    },
    {
      columnKey: 'tags',
      // Labeled header so the column is identifiable in the Columns menu and
      // table even when no visible customer has tags — a blank header over
      // empty cells reads as a missing/broken column (GH #958).
      label: 'Tags',
      sortable: false,
      filterable: false,
      groupable: false,
      render: (customer: Customer) =>
        customer.tags && customer.tags.length > 0 ? (
          <div className={styles.tagsContainer}>
            {customer.tags.map((tag, index) => (
              <Badge
                key={index}
                appearance="outline"
                size="medium"
                title={tag}
                style={{
                  backgroundColor: 'rgb(244, 249, 255)',
                  borderColor: 'rgb(214, 217, 219)',
                  maxWidth: '140px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  lineHeight: '20px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {tag}
              </Badge>
            ))}
          </div>
        ) : null,
    },
    ...(debugMode
      ? [
          {
            columnKey: 'twm_id',
            label: 'TWM ID',
            sortable: true,
            filterable: false,
            groupable: false,
            width: '300px',
            render: (customer: Customer) => (
              <span style={{ fontFamily: 'monospace', fontSize: '14px', color: tokens.colorNeutralForeground2 }}>
                {customer.twm_id || '—'}
              </span>
            ),
          },
          {
            columnKey: 'booqable_id',
            label: 'Booqable ID',
            sortable: true,
            filterable: false,
            groupable: false,
            width: '300px',
            render: (customer: Customer) => (
              <span style={{ fontFamily: 'monospace', fontSize: '14px', color: tokens.colorNeutralForeground2 }}>
                {customer.booqable_id || '—'}
              </span>
            ),
          },
        ] as ColumnConfig<Customer>[]
      : []),
  ];

  const handleSelectionChange = (selectedKeys: string[]) => {
    setSelectedCustomers(selectedKeys);
  };


  // Manual refresh function
  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
  };

  // Listen for Actions menu events
  useEffect(() => {
    const handleEditCustomers = () => {
      if (selectedCustomers.length > 1) {
        setBulkEditModalOpen(true);
      } else if (selectedCustomers.length === 1) {
        navigate(`/customers/${selectedCustomers[0]}`);
      }
    };

    const handleEditTags = () => {
      if (selectedCustomers.length > 0) {
        setEditTagsModalOpen(true);
      }
    };

    const handleGenerateBarcodes = () => {
      if (selectedCustomers.length > 0) {
        setGenerateBarcodesModalOpen(true);
      }
    };

    const handleDeleteCustomers = () => {
      if (selectedCustomers.length > 0) {
        setDeleteModalOpen(true);
      }
    };

    const handleAddNewCustomer = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      setAddInitialKind(detail.kind);
      setAddDialogOpen(true);
    };

    // Handle row menu events
    const handleDeleteCustomer = (event: CustomEvent<string>) => {
      const id = event.detail;
      // Companies route through the dedicated conflict modal — it handles
      // the with-contacts and without-contacts cases in one UI, so we don't
      // stack a generic confirmation in front of it. Non-companies keep the
      // standard ConfirmationDialog.
      const customersCache = queryClient.getQueryData<Customer[]>(
        queryKeys.customers.all,
      );
      const target = customersCache?.find((c) => c.id === id);
      if (target?.customerKind === 'company') {
        setConflictCompanyId(id);
        setConflictCompanyName(target.name || 'this company');
        setConflictModalOpen(true);
        return;
      }
      setDeleteCustomerId(id);
      setDeleteCustomerKind(target?.customerKind as 'individual' | 'contact' | undefined);
      setDeleteSingleModalOpen(true);
    };

    const handleShareCustomer = (event: CustomEvent<Customer>) => {
      setShareCustomer(event.detail);
      setShareDialogOpen(true);
    };

    // Row-menu "Add order" — SPA-navigate to the new-order flow with this
    // customer pre-selected. OrderDetails reads ?customer=<id> on mount.
    // Mirrors the customer profile page's "Add order" button.
    const handleAddOrderForCustomer = (event: CustomEvent<string>) => {
      navigate(`/orders/new?customer=${encodeURIComponent(event.detail)}`);
    };

    // Handle refresh event
    const handleRefreshEvent = () => {
      handleRefresh();
    };

    // Manual Booqable → Supabase reconcile. Webhooks don't fire in local
    // dev, so this is the catch-all for getting the TWM mirror in sync.
    const handleSyncBooqableCustomers = async () => {
      dispatchToast(
        <Toast><ToastTitle>Syncing customers…</ToastTitle></Toast>,
        { intent: 'info' },
      );
      try {
        const response = await apiFetch('/customers/sync', { method: 'POST' });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || `Sync failed (${response.status})`);
        }
        const data = await response.json().catch(() => ({}));
        await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
        const created = data.created ?? 0;
        const updated = data.updated ?? 0;
        const addrs = (data.addresses_created ?? 0) + (data.addresses_updated ?? 0);
        dispatchToast(
          <Toast>
            <ToastTitle>Customers synced</ToastTitle>
            <ToastBody>
              {`${created} created, ${updated} updated${addrs ? ` · ${addrs} addresses` : ''}`}
            </ToastBody>
          </Toast>,
          { intent: created + updated > 0 ? 'success' : 'warning' },
        );
      } catch (err: any) {
        dispatchToast(
          <Toast>
            <ToastTitle>Sync failed</ToastTitle>
            <ToastBody>{err?.message || String(err)}</ToastBody>
          </Toast>,
          { intent: 'error' },
        );
      }
    };

    const handleImportCustomers = () => setImportModalOpen(true);

    window.addEventListener('editCustomers', handleEditCustomers);
    window.addEventListener('editTags', handleEditTags);
    window.addEventListener('generateBarcodes', handleGenerateBarcodes);
    window.addEventListener('deleteCustomers', handleDeleteCustomers);
    window.addEventListener('addNewCustomer', handleAddNewCustomer);
    window.addEventListener('deleteCustomer', handleDeleteCustomer as EventListener);
    window.addEventListener('shareCustomer', handleShareCustomer as EventListener);
    window.addEventListener('addOrderForCustomer', handleAddOrderForCustomer as EventListener);
    window.addEventListener('refreshCustomers', handleRefreshEvent);
    window.addEventListener('syncBooqableCustomers', handleSyncBooqableCustomers);
    window.addEventListener('importCustomers', handleImportCustomers);

    return () => {
      window.removeEventListener('editCustomers', handleEditCustomers);
      window.removeEventListener('editTags', handleEditTags);
      window.removeEventListener('generateBarcodes', handleGenerateBarcodes);
      window.removeEventListener('deleteCustomers', handleDeleteCustomers);
      window.removeEventListener('addNewCustomer', handleAddNewCustomer);
      window.removeEventListener('deleteCustomer', handleDeleteCustomer as EventListener);
      window.removeEventListener('shareCustomer', handleShareCustomer as EventListener);
      window.removeEventListener('addOrderForCustomer', handleAddOrderForCustomer as EventListener);
      window.removeEventListener('refreshCustomers', handleRefreshEvent);
      window.removeEventListener('syncBooqableCustomers', handleSyncBooqableCustomers);
      window.removeEventListener('importCustomers', handleImportCustomers);
    };
  }, [selectedCustomers, navigate, queryClient]);

  // Listen for view mode change events from Header
  useEffect(() => {
    const handleSetViewMode = (event: CustomEvent) => {
      setViewMode(event.detail as ViewMode);
    };

    window.addEventListener('setViewMode', handleSetViewMode as EventListener);
    return () => {
      window.removeEventListener('setViewMode', handleSetViewMode as EventListener);
    };
  }, []);

  // Notify Header of current view mode
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('viewModeChanged', { detail: viewMode }));
  }, [viewMode]);

  const filteredCustomers = useMemo(
    () => filters.applyFilters(customers),
    [filters, customers],
  );

  // Resolve a contextual noun for the bulk-delete dialog. If every selected
  // row shares a kind, use that kind's label (contact/company/customer);
  // otherwise fall back to the generic "customer". "Customer" doubles as the
  // singular for kind === 'individual'.
  const bulkDeleteNoun = useMemo(() => {
    if (selectedCustomers.length === 0) return { singular: 'customer', plural: 'customers' };
    const idSet = new Set(selectedCustomers);
    const kinds = new Set(
      customers.filter((c) => idSet.has(c.id)).map((c) => c.customerKind || 'individual'),
    );
    if (kinds.size !== 1) return { singular: 'customer', plural: 'customers' };
    const only = kinds.values().next().value;
    if (only === 'contact') return { singular: 'contact', plural: 'contacts' };
    if (only === 'company') return { singular: 'company', plural: 'companies' };
    return { singular: 'customer', plural: 'customers' };
  }, [selectedCustomers, customers]);

  // Apply the live search on top of the sidebar-filtered set.
  const searchedCustomers = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return filteredCustomers;
    return filteredCustomers.filter((c) =>
      (c.name || '').toLowerCase().includes(q)
      || (c.email || '').toLowerCase().includes(q)
      || (c.phone || '').toLowerCase().includes(q)
      || (c.company || '').toLowerCase().includes(q),
    );
  }, [filteredCustomers, tableSearch]);

  // Filter columns based on view mode
  const displayColumns =
    viewMode === 'list'
      ? customerColumns.filter((col) => col.columnKey === 'name')
      : customerColumns;

  return (
    <>
      <TablePage
        title="Customers"
        loading={loading}
        loadingLabel="Loading customers…"
        error={error ? (error instanceof Error ? error.message : 'Failed to fetch customers') : null}
        headerLeftActions={
          <Tooltip
            content={filterPanelCollapsed ? 'Show filters' : 'Hide filters'}
            relationship="label"
            visible={filterTipOpen}
            onVisibleChange={(_, data) => setFilterTipOpen(data.visible)}
          >
            <ToolbarButton
              data-tour="customers.filterToggle"
              icon={<PanelLeft24Regular />}
              onClick={toggleFilterPanel}
              aria-label={filterPanelCollapsed ? 'Show filters' : 'Hide filters'}
            />
          </Tooltip>
        }
        headerActions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isLocalhost() && (
              <Button
                data-tour="customers.sync"
                appearance="secondary"
                icon={<ArrowSync24Regular />}
                onClick={() => window.dispatchEvent(new CustomEvent('syncBooqableCustomers'))}
              >
                Sync Booqable
              </Button>
            )}
            <span data-tour="customers.add" style={{ display: 'inline-flex' }}>
              <AddCustomerSplitButton />
            </span>
          </div>
        }
        sidebar={
          <div data-tour="customers.filtersSidebar" style={{ display: 'flex' }}>
            <CustomerFiltersSidebar filters={filters} customers={customers} collapsed={filterPanelCollapsed} />
          </div>
        }
        searchRow={
          <>
            <Input
              data-tour="customers.search"
              size="large"
              contentBefore={<Search20Regular />}
              placeholder="Filter customers by name, email, phone, company…"
              value={tableSearch}
              onChange={(_e, data) => setTableSearch(data.value)}
              style={{ flexGrow: 1, maxWidth: '480px' }}
            />
            {tableSearch.trim() && selectedCustomers.length === 0 && (
              <span style={{ fontSize: '13px', color: tokens.colorNeutralForeground3 }}>
                {searchedCustomers.length} of {filteredCustomers.length}
              </span>
            )}
          </>
        }
        selectedCount={selectedCustomers.length}
        onClearSelection={() => setSelectedCustomers([])}
        bulkNoun={{ singular: 'customer', plural: 'customers' }}
        bulkActions={[
          {
            key: 'edit',
            label: 'Edit',
            icon: <Edit20Regular />,
            onClick: () => window.dispatchEvent(new Event('editCustomers')),
          },
          {
            key: 'edit-tags',
            label: 'Edit tags',
            icon: <Tag20Regular />,
            onClick: () => window.dispatchEvent(new Event('editTags')),
          },
          {
            key: 'generate-barcodes',
            label: 'Generate barcodes',
            icon: <BarcodeScanner20Regular />,
            onClick: () => window.dispatchEvent(new Event('generateBarcodes')),
            // Barcode generation isn't implemented yet — disable until it is.
            disabled: true,
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <Delete20Regular />,
            onClick: () => window.dispatchEvent(new Event('deleteCustomers')),
          },
        ]}
      >
        <DataTable
          data={searchedCustomers}
          columns={displayColumns}
          keyField="id"
          hideableColumns
          columnVisibilityStorageKey="customers"
          selectedKeys={selectedCustomers}
          onSelectionChange={handleSelectionChange}
          itemsPerPage={25}
          showCheckboxes={viewMode === 'table'}
          primaryColumnKey="name"
          rowMenuItems={rowMenuItems || undefined}
          syncUrlParams={true}
          disableWrapper={true}
          defaultSortColumn="number"
        />
      </TablePage>

      <BulkEditCustomersModal
        isOpen={bulkEditModalOpen}
        onClose={() => setBulkEditModalOpen(false)}
        customerCount={selectedCustomers.length}
        onSave={() => {
          setBulkEditModalOpen(false);
          // Invalidate cache after bulk edit
          queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
        }}
      />
      <EditTagsModal
        isOpen={editTagsModalOpen}
        onClose={() => setEditTagsModalOpen(false)}
        customerCount={selectedCustomers.length}
        onSave={() => {
          setEditTagsModalOpen(false);
          // Invalidate cache after tag edit
          queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
        }}
      />
      <GenerateBarcodesModal
        isOpen={generateBarcodesModalOpen}
        onClose={() => setGenerateBarcodesModalOpen(false)}
        customerCount={selectedCustomers.length}
        onGenerate={() => {
          setGenerateBarcodesModalOpen(false);
        }}
      />
      <ConfirmationDialog
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => {
          bulkDeleteMutation.mutate(selectedCustomers);
          setDeleteModalOpen(false);
          setSelectedCustomers([]);
        }}
        title={`Delete ${selectedCustomers.length} ${selectedCustomers.length === 1 ? bulkDeleteNoun.singular : bulkDeleteNoun.plural}`}
        message={`Are you sure you want to delete ${selectedCustomers.length} ${selectedCustomers.length === 1 ? bulkDeleteNoun.singular : bulkDeleteNoun.plural}?`}
        subtext="This cannot be undone"
        confirmButtonText="Delete"
        variant="danger"
      />
      <ConfirmationDialog
        isOpen={deleteSingleModalOpen}
        onClose={() => {
          setDeleteSingleModalOpen(false);
          setDeleteCustomerId(null);
          setDeleteCustomerKind(undefined);
        }}
        onConfirm={() => {
          if (deleteCustomerId) {
            deleteMutation.mutate(deleteCustomerId);
          }
          setDeleteSingleModalOpen(false);
          setDeleteCustomerId(null);
          setDeleteCustomerKind(undefined);
        }}
        title={`Delete ${deleteCustomerKind === 'contact' ? 'contact' : 'customer'}`}
        message={`Are you sure you want to delete this ${deleteCustomerKind === 'contact' ? 'contact' : 'customer'}?`}
        subtext="This cannot be undone"
        confirmButtonText="Delete"
        variant="danger"
      />
      <CustomerDrawer
        isOpen={addDialogOpen}
        initialKind={addInitialKind}
        onClose={() => {
          setAddDialogOpen(false);
          setAddInitialKind(undefined);
        }}
        onSaved={(customerData) => {
          queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
          // Reflect the entity kind in the toast — Add SplitButton + drawer
          // can produce a Customer (individual), Contact, or Company.
          const kind = (customerData as any)?.customerKind;
          // A Company created via the Add drawer must also refresh the
          // CompanyPicker's options (separate ['linked-company-options']
          // query, 5-min staleTime) — otherwise it won't appear when adding
          // a Contact right after, until the cache goes stale or a refresh.
          if (kind === 'company') {
            queryClient.invalidateQueries({ queryKey: ['linked-company-options'] });
          }
          const entityLabel =
            kind === 'company' ? 'Company' : kind === 'contact' ? 'Contact' : 'Customer';
          dispatchToast(
            <Toast>
              <ToastTitle>{`${entityLabel} created`}</ToastTitle>
              <ToastBody>{`${entityLabel} has been successfully added.`}</ToastBody>
            </Toast>,
            { intent: 'success' }
          );
        }}
      />
      <ImportCustomersModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImported={(count) => {
          queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
          dispatchToast(
            <Toast>
              <ToastTitle>{`${count} customer${count === 1 ? '' : 's'} imported`}</ToastTitle>
              <ToastBody>Booqable sync running in background.</ToastBody>
            </Toast>,
            { intent: 'success' },
          );
        }}
      />
      <ShareDialog
        isOpen={shareDialogOpen}
        onClose={() => {
          setShareDialogOpen(false);
          setShareCustomer(null);
        }}
        itemType="customer"
        itemName={shareCustomer?.name || ''}
        shareUrl={`${BOOQABLE_ADMIN_URL}/customers/${shareCustomer?.booqable_id || shareCustomer?.id}/edit`}
        onCopyLink={() => {
          dispatchToast(
            <Toast>
              <ToastTitle>Link copied</ToastTitle>
              <ToastBody>Share link has been copied to clipboard.</ToastBody>
            </Toast>,
            { intent: 'success' }
          );
        }}
        onSend={(recipients) => {
          dispatchToast(
            <Toast>
              <ToastTitle>Shared</ToastTitle>
              <ToastBody>Customer has been shared with {recipients}.</ToastBody>
            </Toast>,
            { intent: 'success' }
          );
        }}
      />

      {conflictCompanyId && (
        <CompanyDeleteConflictModal
          isOpen={conflictModalOpen}
          onClose={() => {
            setConflictModalOpen(false);
            setConflictCompanyId(null);
            setConflictCompanyName('');
          }}
          companyId={conflictCompanyId}
          companyName={conflictCompanyName}
          onMutated={() => {
            // Reassignment changes contact↔company links; refresh related caches
            // so the company picker / sidebar / contacts list reflect the move.
            queryClient.invalidateQueries({ queryKey: ['linked-company-options'] });
            queryClient.invalidateQueries({ queryKey: ['company-contacts', conflictCompanyId] });
          }}
          onResolved={() => {
            setConflictModalOpen(false);
            const resolvedId = conflictCompanyId;
            const resolvedName = conflictCompanyName;
            setConflictCompanyId(null);
            setConflictCompanyName('');
            // Drop the company immediately so the row visibly clears the moment
            // the modal closes. Cascade-archived contacts get cleared by the
            // follow-up invalidation refetch.
            if (resolvedId) {
              queryClient.setQueriesData<Customer[]>(
                {
                  queryKey: queryKeys.customers.all,
                  predicate: (q: any) => Array.isArray(q.state.data),
                },
                (old) => (old ? old.filter((c) => c.id !== resolvedId) : old),
              );
            }
            queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
            queryClient.invalidateQueries({ queryKey: ['linked-company-options'] });
            dispatchToast(
              <Toast>
                <ToastTitle>{`${resolvedName || 'Company'} deleted`}</ToastTitle>
              </Toast>,
              { intent: 'success' }
            );
          }}
        />
      )}

      <Toaster toasterId={toasterId} />
    </>
  );
}
